import React, { useEffect, useState } from 'react';
import { useGoogleTranslate } from '../../Hooks/useGoogleTranslate';
import { Navbar as BootstrapNavbar, Nav } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLogout } from '../../Hooks/useLogout';
import useUserStore from '../../Stores/UserStore';

import { FaShoppingCart, FaUserCircle } from 'react-icons/fa';
import { LuLogOut } from 'react-icons/lu';
import { RiUserSettingsLine } from 'react-icons/ri';
import BellIcon from '../../assets/images/notification-icon.svg?react';
import LogoutModal from '../LogoutModal/LogoutModal';

import logo from '../../assets/images/home-figma/logo-figma.png';
import googlePlayLogo from '../../assets/images/google-icon.svg';
import appleStoreLogo from '../../assets/images/apple-icon.svg';
import CustomButton from '../CustomButton';
import TableActionDropDown from '../TableActionDropDown/TableActionDropDown';
import { APP_STORE_URLS, isStoreLinkReady } from '../../Utils/Constants/appStores';
import './Header.css';
import { HiQuestionMarkCircle } from 'react-icons/hi';

const FigmaChatIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      d="M12 3C7.03 3 3 6.76 3 11.4c0 2.06.79 3.95 2.11 5.43L4 21l4.6-2.11c1.04.33 2.16.51 3.4.51 4.97 0 9-3.76 9-8.4S16.97 3 12 3z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="9.2" cy="11.2" r="1" fill="currentColor" />
    <circle cx="12" cy="11.2" r="1" fill="currentColor" />
    <circle cx="14.8" cy="11.2" r="1" fill="currentColor" />
  </svg>
);

const Header = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, role = 'guest' } = useUserStore();
  const isAuthenticated = !!user;
  const isHomeHeaderGuest = variant === 'home-header' && !isAuthenticated;
  const authPaths = ['/login', '/signup', '/forget-id', '/forget-id2', '/forget-password', '/forget-password2', '/forget-password3'];
  const isAuthPath = authPaths.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  const isLandingHomePath = location.pathname === '/';
  const useSolidHomeHeader = variant === 'home-header' && (isLandingHomePath || isAuthPath);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [navExpanded, setNavExpanded] = useState(false);
  const logoutMutation = useLogout();

  useEffect(() => {
    setNavExpanded(false);
  }, [location.pathname]);

  const imageBaseUrl = import.meta.env.VITE_REACT_APP_IMAGE_URL;

  const getUserImageUrl = () => {
    if (!user?.image) return null;
    const raw = user.image;
    if (typeof raw === 'string' && (raw.startsWith('http://') || raw.startsWith('https://'))) {
      return raw;
    }
    return imageBaseUrl ? `${imageBaseUrl}${raw}` : raw;
  };

  const getInitials = (name = '') => {
    return name
      ?.split(' ')
      ?.map((word) => word?.[0]?.toUpperCase())
      ?.join('');
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogout = () => {
    logoutMutation.mutate(role);
    setShowLogoutModal(false);
  };

  const homeLabel = useGoogleTranslate('Home');
  const aboutUsLabel = useGoogleTranslate('About Us');
  const contactUsLabel = useGoogleTranslate('Contact Us');
  const giftShopLabel = useGoogleTranslate('Gift Shop');
  const coinsStoreLabel = useGoogleTranslate('Coins Store');
  const subscriptionPlansLabel = useGoogleTranslate('Subscription Plans');
  const myProfileLabel = useGoogleTranslate('My Profile');
  const blockedUsersLabel = useGoogleTranslate('Blocked Users');
  const referAFriendLabel = useGoogleTranslate('Refer a Friend');
  const affiliateProgramLabel = useGoogleTranslate('Affiliate Program');
  const settingsLabel = useGoogleTranslate('Settings');
  const helpCenterLabel = useGoogleTranslate('Help Center');
  const logoutLabel = useGoogleTranslate('Logout');
  const registerLabel = useGoogleTranslate('Sign up');
  const loginLabel = useGoogleTranslate('Login');
  const howToUseLabel = useGoogleTranslate('How to Use');

  const navigationItems = [
    { label: homeLabel, link: '/' },
    { label: aboutUsLabel, link: '/about-us' },
    { label: howToUseLabel, link: '/how-to-use' },
    { label: contactUsLabel, link: '/contact-us' },
    { label: helpCenterLabel, link: '/help-center' },
  ];

  const authenticatedNavItems = [
    { label: homeLabel, link: '/home' },
    { label: aboutUsLabel, link: '/about-us' },
    { label: howToUseLabel, link: '/how-to-use' },
    { label: giftShopLabel, link: '/gift-store' },
    { label: coinsStoreLabel, link: '/coin-store' },
    { label: subscriptionPlansLabel, link: '/subscription-plans' },
    { label: contactUsLabel, link: '/contact-us' },
  ];

  const handleStoreClick = (event, url) => {
    if (!isStoreLinkReady(url)) {
      event.preventDefault();
    }
  };

  const storeButtons = (
    <div className="header-store-buttons">
      <a
        href={APP_STORE_URLS.googlePlay}
        className="header-store-btn"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get it on Google Play"
        onClick={(e) => handleStoreClick(e, APP_STORE_URLS.googlePlay)}
      >
        <img src={googlePlayLogo} alt="" className="header-store-logo" />
      </a>
      <a
        href={APP_STORE_URLS.appleAppStore}
        className="header-store-btn header-store-btn--apple"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Download on the App Store"
        onClick={(e) => handleStoreClick(e, APP_STORE_URLS.appleAppStore)}
      >
        <img src={appleStoreLogo} alt="" className="header-store-logo" />
      </a>
    </div>
  );

  return (
    <BootstrapNavbar
      className={`mainHeaderBar site-header-nav ${variant}`}
      collapseOnSelect
      expanded={navExpanded}
      expand="lg"
      onToggle={setNavExpanded}
      variant="light"
    >
      <div className={`header-main-container ${useSolidHomeHeader ? 'home-header-container--glass' : 'home-header-container--transparent'}`}>
        <div className="header-brand-row">
          <BootstrapNavbar.Brand as={Link} to="/" className="navbar-brand">
            <img src={logo} alt="" width={100} />
          </BootstrapNavbar.Brand>

          <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" />
        </div>

        <BootstrapNavbar.Collapse id="basic-navbar-nav">
          {/* Navigation Menu */}
          <Nav className={`mx-auto gap-4 ${isHomeHeaderGuest ? 'home-header-nav-links' : ''}`}>
            {(isAuthenticated ? authenticatedNavItems : navigationItems).map(
              (item, index) => (
                <Nav.Link
                  key={index}
                  as={Link}
                  to={item.link}
                  className={`nav-link ${isHomeHeaderGuest && location.pathname === item.link ? 'home-header-active-link' : ''}`}
                >
                  {item.icon && <item.icon className="nav-icon" />}
                  {item.label}
                </Nav.Link>
              )
            )}
          </Nav>

          {/* Right Side Actions */}
          <div className="d-flex gap-2 gap-sm-3 align-items-center">
            {isAuthenticated ? (
              <>
                {/* Support Button for non-admin users */}
                {role !== 'admin' && (
                  <div className="d-flex gap-3">
                    <button
                      className="transparent-btn header-action-icon-btn"
                      onClick={() => {
                        setNavExpanded(false);
                        navigate('/chat');
                      }}
                    >
                      <FigmaChatIcon />
                    </button>
                    <button
                      className="transparent-btn header-action-icon-btn"
                      onClick={() => {
                        setNavExpanded(false);
                        navigate('/notifications');
                      }}
                    >
                      <BellIcon />
                    </button>
                  </div>
                )}



                {/* User Dropdown */}
                <TableActionDropDown
                  actions={[
                    {
                      name: myProfileLabel,
                      icon: 'profile',
                      onClick: () => {
                        if (user?.role === 'admin') {
                          navigate('/admin/profile');
                        } else {
                          navigate('/profile');
                        }
                      },
                    },
                    {
                      name: blockedUsersLabel,
                      icon: 'block-list',
                      onClick: () => {
                        navigate('/blocked-users');
                      },
                    },
                    {
                      name: referAFriendLabel,
                      icon: 'refer-a-friend',
                      onClick: () => {
                        navigate('/refer-a-friend');
                      },
                    },
                    {
                      name: affiliateProgramLabel,
                      icon: 'refer-a-friend',
                      onClick: () => {
                        navigate('/affiliate');
                      },
                    },
                    {
                      name: settingsLabel,
                      icon: 'settings',
                      onClick: () => {
                        navigate('/settings');
                      },
                    },
                    {
                      name: helpCenterLabel,
                      icon: 'help-center',
                      onClick: () => {
                        navigate('/help-center');
                      },
                    },
                    {
                      name: logoutLabel,
                      icon: 'logout',
                      onClick: handleLogoutClick,
                    },
                  ]}
                >
                  <div className="userImage gap-1 beechMein">
                    {getUserImageUrl() ? (
                      <img src={getUserImageUrl()} alt="" className="img-fluid profile-pic" />
                    ) : (
                      <FaUserCircle size={30} />
                    )}
                    <h6>{user?.full_name}</h6>
                  </div>
                </TableActionDropDown>
                {storeButtons}
              </>
            ) : (
              <>
                {/* Guest User Actions */}
                {isHomeHeaderGuest ? (
                  <div className="home-header-auth-actions guest-auth-actions">
                    <Link to="/login" className="home-header-login-btn">
                      {loginLabel}
                    </Link>
                    <Link to="/signup" className="home-header-signup-btn">
                      {registerLabel}
                    </Link>
                    {storeButtons}
                  </div>
                ) : (
                  <div className="guest-auth-actions">
                    <CustomButton
                      onClick={() => navigate('/signup')}
                    >
                      {registerLabel}
                    </CustomButton>
                    <CustomButton
                      onClick={() => navigate('/login')}
                      variant="secondaryButton"
                    >
                      {loginLabel}
                    </CustomButton>
                    {storeButtons}
                  </div>
                )}
              </>
            )}
          </div>
        </BootstrapNavbar.Collapse>
      </div>
      {/* Logout Modal */}
      <LogoutModal
        show={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onLogout={handleLogout}
        isLoading={logoutMutation.isPending}
      />
    </BootstrapNavbar>
  );
};

export default Header;
